class CustomVideo extends HTMLElement {
  constructor() {
    super();
    this._isInitialized = false;
    this._initTimeoutId = null;
    this._cleanup = [];
    this._visibilityObserver = null;
    this._hasLoadedVideoSources = false;
  }

  connectedCallback() {
    if (this._isInitialized) return;
    this._isInitialized = true;

    // Wait for Light DOM children to be parsed
    this._initTimeoutId = window.setTimeout(() => {
      const wrapper = this;
      const video = wrapper.querySelector('video');
      const playBtn = wrapper.querySelector('.play-pause');
      const centralPlayBtn = wrapper.querySelector('[data-hero-card-play-pause]');
      const progress = wrapper.querySelector('.progress');
      const muteBtn = wrapper.querySelector('.mute');
      const fullscreenBtn = wrapper.querySelector('.fullscreen');
      const timeDisplay = wrapper.querySelector('.time-display');
      const timeDisplayText = wrapper.querySelector('.time-display-text');

      if (!video) return;

      let hasAutoPausedOnce = false;

      // Load poster image immediately
      const deferredPoster = video.getAttribute('data-poster');
      if (deferredPoster) {
        video.setAttribute('poster', deferredPoster);
        video.removeAttribute('data-poster');
      }

      const shouldAutoplay = video.hasAttribute('autoplay');
      const sourceEls = video.querySelectorAll('source[data-src]');

      const loadVideoSources = () => {
        if (this._hasLoadedVideoSources) return;

        for (const sourceEl of sourceEls) {
          const deferredSrc = sourceEl.getAttribute('data-src');
          if (!deferredSrc) continue;
          sourceEl.setAttribute('src', deferredSrc);
          sourceEl.removeAttribute('data-src');
        }

        const deferredVideoSrc = video.getAttribute('data-src');
        if (deferredVideoSrc) {
          video.setAttribute('src', deferredVideoSrc);
          video.removeAttribute('data-src');
        }

        this._hasLoadedVideoSources = true;
        video.preload = 'metadata';
        video.load();
      };

      const playVideo = () => {
        const attemptPlay = () => {
          video.play?.().catch(() => {});
        };

        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          attemptPlay();
          return;
        }

        video.addEventListener('canplay', attemptPlay, { once: true });
        attemptPlay();
      };

      const activateVideo = () => {
        loadVideoSources();
        if (shouldAutoplay) playVideo();
      };

      const deactivateVideo = () => {
        if (!video.paused) video.pause();
        hasAutoPausedOnce = false;
        video.currentTime = 0;
      };

      const slideshow = this.closest('slideshow-container');

      const shouldAutoplayInCarousel = () => {
        const slide = this.closest('.swiper-slide');
        if (!slide || !slideshow) return true;

        const firstSlide = slideshow.querySelector('.swiper-wrapper > .swiper-slide');
        const isFirstSlide = slide === firstSlide;
        const isVisible = slide.classList.contains('swiper-slide-active');
        const swiperReady = slideshow.dataset.initialized === 'true';

        if (isVisible) return true;
        if (isFirstSlide && !swiperReady) return true;

        return false;
      };

      if (slideshow) {
        const syncWithCarousel = () => {
          if (shouldAutoplayInCarousel()) {
            activateVideo();
            return;
          }

          deactivateVideo();
        };

        const onSlideshowEvent = () => {
          setTimeout(syncWithCarousel, 50);
        };

        for (const eventName of [
          'swiper:init',
          'swiper:slideChange',
          'swiper:slideChangeTransitionStart',
        ]) {
          slideshow.addEventListener(eventName, onSlideshowEvent);
          this._cleanup.push(() => slideshow.removeEventListener(eventName, onSlideshowEvent));
        }

        syncWithCarousel();

        if (slideshow.dataset.initialized !== 'true') {
          window.setTimeout(syncWithCarousel, 100);
        }

        requestAnimationFrame(syncWithCarousel);
      } else if ('IntersectionObserver' in window) {
        this._visibilityObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                activateVideo();
                continue;
              }

              deactivateVideo();
            }
          },
          {
            root: null,
            rootMargin: '200px 0px',
            threshold: 0.01,
          }
        );

        this._visibilityObserver.observe(wrapper);
      } else {
        activateVideo();
      }

      // 🔥 Prevent video from taking focus
      video.setAttribute('tabindex', '-1');

      let liveRegion = wrapper.querySelector('.sr-only');
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.className = 'sr-only';
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        wrapper.appendChild(liveRegion);
      }

      function formatTime(time) {
        if (isNaN(time)) return '0:00';
        const m = Math.floor(time / 60);
        const s = Math.floor(time % 60)
          .toString()
          .padStart(2, '0');
        return `${m}:${s}`;
      }

      function formatSpokenTime(time) {
        if (isNaN(time)) return '0 seconds';
        const m = Math.floor(time / 60);
        const s = Math.floor(time % 60);
        if (m > 0) {
          return `${m} minute${m !== 1 ? 's' : ''} ${s} second${s !== 1 ? 's' : ''}`;
        }
        return `${s} second${s !== 1 ? 's' : ''}`;
      }

      let duration = video.duration && !isNaN(video.duration) ? video.duration : 0;

      const onLoadedMetadata = () => {
        duration = video.duration;
        if (progress) progress.setAttribute('aria-valuemax', Math.floor(duration));
        if (timeDisplay && timeDisplayText) {
          timeDisplayText.textContent = `${formatTime(video.currentTime)} / ${formatTime(duration)}`;
          timeDisplay.setAttribute(
            'aria-label',
            `Time ${formatSpokenTime(video.currentTime)} / ${formatSpokenTime(duration)}`
          );
        }
      };
      video.addEventListener('loadedmetadata', onLoadedMetadata);
      this._cleanup.push(() => video.removeEventListener('loadedmetadata', onLoadedMetadata));

      if (duration > 0 && timeDisplay && timeDisplayText) {
        timeDisplayText.textContent = `${formatTime(video.currentTime)} / ${formatTime(duration)}`;
        timeDisplay.setAttribute(
          'aria-label',
          `Time ${formatSpokenTime(video.currentTime)} / ${formatSpokenTime(duration)}`
        );
      }

      const togglePlay = () => {
        if (video.paused) {
          activateVideo();
          if (!shouldAutoplay) playVideo();
          return;
        }

        video.pause();
      };

      if (playBtn) {
        const onPlayBtnClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          togglePlay();
        };
        playBtn.addEventListener('click', onPlayBtnClick);
        this._cleanup.push(() => playBtn.removeEventListener('click', onPlayBtnClick));
      }

      if (centralPlayBtn) {
        const onCentralPlayClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          togglePlay();
        };
        centralPlayBtn.addEventListener('click', onCentralPlayClick);
        this._cleanup.push(() => centralPlayBtn.removeEventListener('click', onCentralPlayClick));
      }

      const updateCentralPlayBtnUI = (paused) => {
        wrapper.classList.toggle('is-paused', paused);
        if (!centralPlayBtn) return;
        const playIcon = centralPlayBtn.querySelector('[data-hero-card-icon="play"]');
        const pauseIcon = centralPlayBtn.querySelector('[data-hero-card-icon="pause"]');
        const initialLabel = centralPlayBtn.getAttribute('aria-label') || 'Play video';
        const labelDesc = initialLabel.replace(/^(Play|Pause)\s+/i, '');

        centralPlayBtn.setAttribute(
          'aria-label',
          (paused ? 'Play ' : 'Pause ') + (labelDesc || 'video')
        );

        if (playIcon) playIcon.hidden = !paused;
        if (pauseIcon) pauseIcon.hidden = paused;
      };

      // 🔥 Keyboard fix
      wrapper.querySelectorAll('button').forEach((btn) => {
        const onButtonKeydown = (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            btn.click();
          }
        };
        btn.addEventListener('keydown', onButtonKeydown);
        this._cleanup.push(() => btn.removeEventListener('keydown', onButtonKeydown));
      });

      // ✅ Visual update only (NO aria updates)
      const onTimeUpdate = () => {
        duration = video.duration && !isNaN(video.duration) ? video.duration : duration;

        if (!isNaN(duration) && duration > 0) {
          const percent = (video.currentTime / duration) * 100;

          if (progress) {
            progress.style.setProperty('--progress', percent + '%');

            // ✅ FIX: keep aria in sync
            progress.setAttribute('aria-valuenow', Math.floor(video.currentTime));
            progress.setAttribute('aria-valuetext', formatSpokenTime(video.currentTime));
          }

          if (timeDisplay && timeDisplayText) {
            timeDisplayText.textContent = `${formatTime(video.currentTime)} / ${formatTime(duration)}`;
            timeDisplay.setAttribute(
              'aria-label',
              `Time ${formatSpokenTime(video.currentTime)} / ${formatSpokenTime(duration)}`
            );
          }
        }
      };
      video.addEventListener('timeupdate', onTimeUpdate);
      this._cleanup.push(() => video.removeEventListener('timeupdate', onTimeUpdate));

      if (progress) {
        const onProgressFocusin = () => {
          progress.setAttribute('aria-valuetext', formatSpokenTime(video.currentTime));
          progress.setAttribute('aria-valuenow', Math.floor(video.currentTime));
        };
        progress.addEventListener('focusin', onProgressFocusin);
        this._cleanup.push(() => progress.removeEventListener('focusin', onProgressFocusin));

        const onProgressKeydown = (e) => {
          const step = 5;
          if (!duration || isNaN(duration)) return;

          if (['ArrowRight', 'ArrowLeft'].includes(e.key)) {
            e.preventDefault();
            e.stopPropagation();

            if (e.key === 'ArrowRight') {
              video.currentTime = Math.min(video.currentTime + step, duration);
            }

            if (e.key === 'ArrowLeft') {
              video.currentTime = Math.max(video.currentTime - step, 0);
            }

            progress.setAttribute('aria-valuenow', Math.floor(video.currentTime));
            progress.setAttribute('aria-valuetext', formatSpokenTime(video.currentTime));

            const currentSpoken = formatSpokenTime(video.currentTime);
            const totalSpoken = formatSpokenTime(duration);

            liveRegion.textContent = `Time ${currentSpoken} of ${totalSpoken}`;
          }
        };
        progress.addEventListener('keydown', onProgressKeydown);
        this._cleanup.push(() => progress.removeEventListener('keydown', onProgressKeydown));

        const onProgressClick = (e) => {
          if (!duration || isNaN(duration)) return;
          const rect = progress.getBoundingClientRect();
          const clickX = e.clientX - rect.left;
          const width = rect.width;
          if (!width) return;

          const percent = clickX / width;
          const newTime = percent * duration;

          video.currentTime = newTime;

          // update UI
          progress.style.setProperty('--progress', percent * 100 + '%');

          // update aria ONLY on user interaction
          progress.setAttribute('aria-valuenow', Math.floor(newTime));
          progress.setAttribute('aria-valuetext', formatSpokenTime(newTime));

          // announce (optional but good)
          const currentSpoken = formatSpokenTime(newTime);
          const totalSpoken = formatSpokenTime(duration);

          liveRegion.textContent = `Time ${currentSpoken} of ${totalSpoken}`;

          // focus for accessibility (Safari fix)
          progress.focus();
        };
        progress.addEventListener('click', onProgressClick);
        this._cleanup.push(() => progress.removeEventListener('click', onProgressClick));
      }

      if (muteBtn) {
        // 🔊 Mute
        const onMuteClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          video.muted = !video.muted;
          muteBtn.setAttribute('data-mute', video.muted);
          muteBtn.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
        };
        muteBtn.addEventListener('click', onMuteClick);
        this._cleanup.push(() => muteBtn.removeEventListener('click', onMuteClick));
      }

      if (fullscreenBtn) {
        // iOS Safari doesn't allow requestFullscreen on custom elements.
        // Use CSS "fake fullscreen" on iOS so custom controls remain visible.
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

        let iosFakeFullscreen = false;

        const updateFullscreenUI = (inFullscreen) => {
          fullscreenBtn.setAttribute('aria-label', inFullscreen ? 'Exit fullscreen' : 'Fullscreen');
          fullscreenBtn.setAttribute('data-video-maximize', inFullscreen ? 'false' : 'true');
        };

        const enterFullscreen = () => {
          if (isIOS) {
            // ✅ Use native iOS video fullscreen
            if (video.webkitEnterFullscreen) {
              video.webkitEnterFullscreen();
            } else {
              // fallback (fake fullscreen)
              iosFakeFullscreen = true;
              wrapper.classList.add('ios-fake-fullscreen');
            }
            updateFullscreenUI(true);
            requestAnimationFrame(() => fullscreenBtn.focus());
          } else {
            (wrapper.requestFullscreen || wrapper.webkitRequestFullscreen)?.call(wrapper);
          }
        };

        const exitFullscreen = () => {
          if (isIOS) {
            iosFakeFullscreen = false;
            wrapper.classList.remove('ios-fake-fullscreen');
            updateFullscreenUI(false);
            requestAnimationFrame(() => fullscreenBtn.focus());
          } else {
            (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
          }
        };

        const isInFullscreen = () =>
          isIOS
            ? iosFakeFullscreen
            : !!(document.fullscreenElement || document.webkitFullscreenElement);

        const onFullscreenClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          isInFullscreen() ? exitFullscreen() : enterFullscreen();
        };
        fullscreenBtn.addEventListener('click', onFullscreenClick);
        this._cleanup.push(() => fullscreenBtn.removeEventListener('click', onFullscreenClick));

        // Sync button state when real fullscreen exits via Escape key or browser UI (non-iOS)
        if (!isIOS) {
          const onFullscreenChange = () => {
            updateFullscreenUI(isInFullscreen());
            // Restore focus to fullscreen button after transition (macOS Safari drops focus)
            requestAnimationFrame(() => fullscreenBtn.focus());
          };
          document.addEventListener('fullscreenchange', onFullscreenChange);
          document.addEventListener('webkitfullscreenchange', onFullscreenChange);
          this._cleanup.push(() => {
            document.removeEventListener('fullscreenchange', onFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
          });
        }
      }

      // AUTO PAUSE LOGIC
      const shouldAutoPause = wrapper.getAttribute('data-auto-pause') === 'true';
      const defaultPauseSeconds = 5;
      let pauseAfterSecondsAttr = wrapper.getAttribute('pause-after-seconds');
      if (!pauseAfterSecondsAttr) {
        const heroCard = wrapper.closest('hero-media-card');
        if (heroCard) {
          pauseAfterSecondsAttr = heroCard.getAttribute('pause-after-seconds');
        }
      }
      const parsedPauseAfterSeconds = pauseAfterSecondsAttr
        ? parseInt(pauseAfterSecondsAttr, 10)
        : defaultPauseSeconds;
      const pauseAfterSeconds =
        Number.isFinite(parsedPauseAfterSeconds) && parsedPauseAfterSeconds > 0
          ? parsedPauseAfterSeconds
          : defaultPauseSeconds;

      let autoPauseListener = null;
      hasAutoPausedOnce = false;

      const clearAutoPause = () => {
        if (autoPauseListener) {
          video.removeEventListener('timeupdate', autoPauseListener);
          autoPauseListener = null;
        }
      };

      const scheduleAutoPause = () => {
        if (!shouldAutoPause || pauseAfterSeconds <= 0 || hasAutoPausedOnce) return;
        clearAutoPause();
        autoPauseListener = () => {
          if (video.currentTime >= pauseAfterSeconds) {
            hasAutoPausedOnce = true;
            clearAutoPause();
            video.currentTime = pauseAfterSeconds;
            video.pause();
          }
        };
        video.addEventListener('timeupdate', autoPauseListener);
      };

      const onVideoPlay = () => {
        if (playBtn) {
          //   playBtn.textContent = '⏸';
          playBtn.setAttribute('aria-label', 'Pause video');
          playBtn.setAttribute('data-video-play', 'false');
        }
        updateCentralPlayBtnUI(false);
        if (shouldAutoPause) {
          scheduleAutoPause();
        }
      };
      video.addEventListener('play', onVideoPlay);
      this._cleanup.push(() => video.removeEventListener('play', onVideoPlay));

      const onVideoPause = () => {
        if (playBtn) {
          //   playBtn.textContent = '▶';
          playBtn.setAttribute('aria-label', 'Play video');
          playBtn.setAttribute('data-video-play', 'true');
        }
        updateCentralPlayBtnUI(true);
        clearAutoPause();
      };
      video.addEventListener('pause', onVideoPause);
      this._cleanup.push(() => video.removeEventListener('pause', onVideoPause));

      if (!video.paused) {
        if (shouldAutoPause) {
          scheduleAutoPause();
        }
        updateCentralPlayBtnUI(false);
      } else {
        updateCentralPlayBtnUI(true);
      }

      // 🔥 FIXED: only video click (not wrapper)
      video.addEventListener('click', togglePlay);
      this._cleanup.push(() => video.removeEventListener('click', togglePlay));
    }, 0);
  }

  disconnectedCallback() {
    if (this._initTimeoutId) {
      window.clearTimeout(this._initTimeoutId);
      this._initTimeoutId = null;
    }

    if (this._visibilityObserver) {
      this._visibilityObserver.disconnect();
      this._visibilityObserver = null;
    }

    for (const cleanupFn of this._cleanup) {
      cleanupFn();
    }

    this._cleanup = [];
    this._isInitialized = false;
    this._hasLoadedVideoSources = false;
  }
}

if (!customElements.get('custom-video')) {
  customElements.define('custom-video', CustomVideo);
}
